import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { SystemsService } from '@twaice-fe/frontend/shared/services';
import { catchError, concatMap, debounceTime, filter, map, of, switchMap, takeUntil, withLatestFrom } from 'rxjs';

import { NavigationEnd, Router } from '@angular/router';
import { updateQueryParameter } from '@twaice-fe/frontend/shared/utilities';
import { from } from 'rxjs';

import { ActivatedRoute } from '@angular/router';
import { getUnixTimestampFromDate } from '@twaice-fe/shared/utilities';
import { systemActions } from '../actions';
import { systemSelectors } from '../selectors';

@Injectable()
export class SystemsEffects {
  fetchSystemDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemActions.fetchSystemDetails),
      switchMap(() =>
        this.systemService.fetchSystemDetails().pipe(
          map((systemDetails) => systemActions.loadSystemDetailsSuccess({ systemDetails })),
          takeUntil(this.actions$.pipe(ofType(systemActions.cancelSystemDetailsRequest))),
          catchError((error) => {
            console.error('[Error/Fetch system details]', error);
            return of(systemActions.loadSystemDetailsFailure({ error }));
          })
        )
      )
    )
  );

  // redirect to new system route when system changes
  selectSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemActions.selectSystem),
      switchMap(({ systemId }) => this.store.select(systemSelectors.getSystemById(systemId))),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      switchMap(([currentSystem, previousSystem]) => {
        if (!previousSystem || previousSystem.systemBk === currentSystem?.systemBk) {
          return of(systemActions.systemRouteSettled({ systemId: currentSystem?.id }));
        }

        const routeSegments = this.router.routerState.snapshot.url.split('/');

        // replace old systemBK and customerBk in route with new ones and drop query params
        const newRoute = routeSegments.map((segment, i) => {
          // Update systemBk
          if (segment === previousSystem?.systemBk && routeSegments[i - 1] === 'system') {
            return currentSystem?.systemBk;
          }
          // Update customerBk
          if (segment === previousSystem?.customerBk && routeSegments[i - 1] === 'customer') {
            return currentSystem?.customerBk;
          }
          return segment.split('?')[0];
        });

        return from(
          this.router.navigate(newRoute, {
            queryParamsHandling: 'merge',
          })
        ).pipe(map(() => systemActions.systemRouteSettled({ systemId: currentSystem?.id })));
      })
    )
  );
  // Having all navigation side effects in a single effect allows us to handle them synchronously and avoid navigation conflicts
  syncTimeIntoUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemActions.setTimeRange, systemActions.setTimeResolution),
      concatMap((action) => {
        // Data Explorer owns its own URL — skip all time URL writes on DE routes
        if (this.router.url.includes('data-explorer')) {
          return of(systemActions.timeRangeUrlSyncSuccess());
        }

        switch (action.type) {
          case systemActions.setTimeRange.type:
            return from(
              updateQueryParameter(
                this.router,
                this.route,
                ['timeRange', 'startDate', 'endDate'],
                [
                  action.timeRange.id,
                  getUnixTimestampFromDate(action.timeRange.value[0] as Date),
                  getUnixTimestampFromDate(action.timeRange.value[1] as Date),
                ]
              )
            ).pipe(map(() => systemActions.timeRangeUrlSyncSuccess()));

          case systemActions.setTimeResolution.type:
            return from(updateQueryParameter(this.router, this.route, ['timeRes'], [action.granularity.value])).pipe(
              map(() => systemActions.timeRangeUrlSyncSuccess())
            );
        }
      })
    )
  );

  syncDateValueIntoUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(systemActions.setDateValue),
        concatMap((action) =>
          from(updateQueryParameter(this.router, this.route, 'date', action.date.toISOString().split('T')[0]))
        )
      ),
    { dispatch: false }
  );

  // if we ever lose the selected time range in the URL, we resync it from the store
  keepSelectedTimeRangeInUrl$ = createEffect(() =>
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      filter((event) => !event.url.includes('data-explorer')),
      filter((event) => !event.url.includes('timeRange') && !event.url.includes('startDate') && !event.url.includes('endDate')),
      debounceTime(50),
      withLatestFrom(
        this.store.select(systemSelectors.getSelectedTimeRange),
        this.store.select(systemSelectors.getIsUserSelectedTimeRange)
      ),
      filter(([, _, isUserSelectedTimeRange]) => !!isUserSelectedTimeRange),
      map(([_, selectedTimeRange]) => {
        updateQueryParameter(
          this.router,
          this.route,
          ['timeRange', 'startDate', 'endDate'],
          [
            selectedTimeRange?.timeRange?.id,
            getUnixTimestampFromDate(selectedTimeRange?.timeRange?.value[0] as Date),
            getUnixTimestampFromDate(selectedTimeRange?.timeRange?.value[1] as Date),
          ]
        );
        return systemActions.timeRangeUrlSyncSuccess();
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private router: Router,
    private route: ActivatedRoute,
    protected store: Store,
    private systemService: SystemsService
  ) {}
}
